import React from 'react';
import { useLocale } from '../contexts/LocaleContext';
import { RichTextField, type MentionableEntity } from './RichTextField';
import { EditDialogFooter } from './EditDialogFooter';
import {
  ENTITY_DETAIL_DIALOG_CONTENT_CLASSNAME,
  EntityDetailModalLayout,
  EntityDetailModalTitle,
} from './EntityDetailModalLayout';
import { SceneTitleAutocomplete } from './SceneTitleAutocomplete';
import { RelatedEntityLink } from './RelatedEntityLink';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from './ui/dialog';
import type { Scene } from './Scenes';
import type { RelatedEntity } from '../utils/entity-utils';

interface SceneFormDialogProps {
  open: boolean;
  isEditing: boolean;
  isSaving: boolean;
  title: string;
  description: string;
  mentionableEntities: MentionableEntity[];
  linkableScenes: Scene[];
  projectId?: string;
  relatedLinksTo?: RelatedEntity[];
  relatedReferencedIn?: RelatedEntity[];
  deleteDisabled?: boolean;
  unlinkDisabled?: boolean;
  showUnlink?: boolean;
  onTitleChange: (title: string) => void;
  onDescriptionChange: (description: string) => void;
  onSelectExistingScene: (scene: Scene) => void;
  onSave: () => void;
  onCancel: () => void;
  onDelete?: () => void;
  onUnlink?: () => void;
  onRemoveLinksTo?: (entity: RelatedEntity) => void;
  onRemoveReferencedIn?: (entity: RelatedEntity) => void;
}

export const SceneFormDialog: React.FC<SceneFormDialogProps> = ({
  open,
  isEditing,
  isSaving,
  title,
  description,
  mentionableEntities,
  linkableScenes,
  projectId,
  relatedLinksTo = [],
  relatedReferencedIn = [],
  deleteDisabled = false,
  unlinkDisabled = false,
  showUnlink = false,
  onTitleChange,
  onDescriptionChange,
  onSelectExistingScene,
  onSave,
  onCancel,
  onDelete,
  onUnlink,
  onRemoveLinksTo,
  onRemoveReferencedIn,
}) => {
  const { t } = useLocale();
  const showRelatedEntities =
    Boolean(projectId) &&
    isEditing &&
    (relatedLinksTo.length > 0 || relatedReferencedIn.length > 0);

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && !isSaving && onCancel()}>
      <DialogContent className={ENTITY_DETAIL_DIALOG_CONTENT_CLASSNAME}>
        <DialogTitle className="sr-only">
          {isEditing ? t('edit_scene') : t('add_or_link_scene')}
        </DialogTitle>
        <EntityDetailModalLayout
          header={
            <EntityDetailModalTitle>
              {isEditing ? t('edit_scene') : t('add_or_link_scene')}
            </EntityDetailModalTitle>
          }
          footer={
            <EditDialogFooter
              isEditing={isEditing}
              isSaving={isSaving}
              saveDisabled={!title.trim()}
              onCancel={onCancel}
              onSave={onSave}
              onDelete={isEditing ? onDelete : undefined}
              deleteDisabled={deleteDisabled}
              onUnlink={isEditing && showUnlink ? onUnlink : undefined}
              unlinkDisabled={unlinkDisabled}
            />
          }
        >
          <div className="space-y-6 p-4">
            <div>
              <Label className="mb-2 block">
                {t('title')} *
              </Label>
              {isEditing ? (
                <Input
                  type="text"
                  value={title}
                  onChange={(event) => onTitleChange(event.target.value)}
                  placeholder={t('scene_title_placeholder')}
                  disabled={isSaving}
                  autoFocus
                />
              ) : (
                <>
                  <SceneTitleAutocomplete
                    value={title}
                    onChange={onTitleChange}
                    scenes={linkableScenes}
                    onSelectScene={onSelectExistingScene}
                    placeholder={t('scene_title_placeholder')}
                    disabled={isSaving}
                    autoFocus
                  />
                  <p className="mt-1.5 text-sm text-muted-foreground">
                    {t('link_existing_scene_hint')}
                  </p>
                </>
              )}
            </div>
            <div>
              <Label className="mb-2 block">{t('description')}</Label>
              <RichTextField
                value={description}
                onChange={onDescriptionChange}
                placeholder={t('scene_description_placeholder')}
                minHeight="150px"
                mentionableEntities={mentionableEntities}
              />
            </div>
            {showRelatedEntities && projectId && (
              <div>
                <Label className="mb-2 block font-semibold">{t('related_entities')}</Label>
                <div className="flex flex-col gap-3">
                  {relatedLinksTo.length > 0 && (
                    <div>
                      <span className="mb-1 block text-xs text-muted-foreground">{t('links_to')}</span>
                      <div className="flex flex-wrap gap-2">
                        {relatedLinksTo.map((entity) => (
                          <RelatedEntityLink
                            key={`${entity.type}-${entity.id}-${entity.documentId ?? ''}`}
                            entity={entity}
                            projectId={projectId}
                            onRemove={onRemoveLinksTo ? () => onRemoveLinksTo(entity) : undefined}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {relatedReferencedIn.length > 0 && (
                    <div>
                      <span className="mb-1 block text-xs text-muted-foreground">{t('referenced_in')}</span>
                      <div className="flex flex-wrap gap-2">
                        {relatedReferencedIn.map((entity) => (
                          <RelatedEntityLink
                            key={`${entity.type}-${entity.id}-${entity.documentId ?? ''}`}
                            entity={entity}
                            projectId={projectId}
                            onRemove={
                              onRemoveReferencedIn ? () => onRemoveReferencedIn(entity) : undefined
                            }
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </EntityDetailModalLayout>
      </DialogContent>
    </Dialog>
  );
};
